import { usePagination } from '@/global/hooks/usePagination';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { formatDateTime, getName } from '@/global/helpers/text';
import { Box, Stack, Typography } from '@mui/material';
import { apiSchoolLogs } from '@/modules/school-logs';
import type { SchoolLogsDto } from '@/global/api/gen/ts/SchoolLogsDto';
import { LogTypesMapping } from '@/modules/school-logs/shared/constants';
import type { ValueChangeDto } from '@/global/api/gen/ts/ValueChangeDto';

interface Props {
    id: number;
}

const renderValue = (value: ValueChangeDto | null) => {
    if (!value) return null;

    const res = [];

    if ('aitestPercent' in value) {
        res.push(
            <Stack
                flexDirection={'row'}
                justifyContent={'space-between'}
                key={'aitestPercent'}
            >
                <Typography variant={'subtitle2'}>
                    Процент кешбека за Aitest:
                </Typography>
                <Typography variant={'subtitle2'}>
                    {value.aitestPercent}
                    {' '}
                    %
                </Typography>
            </Stack>
        );
    }

    if ('certificatePercent' in value) {
        res.push(
            <Stack
                flexDirection={'row'}
                justifyContent={'space-between'}
                key={'certificatePercent'}
            >
                <Typography variant={'subtitle2'}>
                    Процент кешбека за Сертификат:
                </Typography>
                <Typography variant={'subtitle2'}>
                    {value.certificatePercent}
                    {' '}
                    %
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack
            width={'100%'}
        >
            {res}
        </Stack>
    );
};

export function SchoolLogsTable({id}: Props) {

    const {api, grid} = usePagination();

    const {data, isLoading} = apiSchoolLogs(id, {...api});

    const columns: GridColDef<SchoolLogsDto>[] = useMemo(() => [
        {field: 'id', headerName: 'ID', maxWidth: 100, width: 100, sortable: false},
        {
            field: 'sum',
            headerName: 'Сумма',
            minWidth: 200,
            valueGetter: ({row}) => LogTypesMapping[row.type]
        },
        {
            field: 'user',
            headerName: 'Пользователь',
            minWidth: 200,
            valueGetter: ({row}) => row.user ? getName(row.user).fullName : 'Неизвестный пользователь',
        },
        {
            field: 'old_value',
            headerName: 'Старое значение',
            minWidth: 400,
            flex: 1,
            renderCell: ({value}) => renderValue(value)
        },
        {
            field: 'new_value',
            headerName: 'Новое значение',
            minWidth: 400,
            flex: 1,
            renderCell: ({value}) => renderValue(value)
        },
        {
            field: 'created_at',
            headerName: 'Дата изменения',
            sortable: false,
            minWidth: 200,
            valueFormatter: ({value}) => formatDateTime(value)
        }
    ], []);

    return (
        <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
            <DataGrid
                {...grid}
                rows={data?.items ?? []}
                columns={columns}
                loading={isLoading}
                rowCount={data?.total ?? 0}
                disableRowSelectionOnClick
                disableColumnMenu={true}
                disableColumnFilter={true}
            />
        </Box>
    );
}